/* eslint-disable react/prop-types */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    AlertTriangle, CheckCircle2, Clock, Info, RefreshCw, Smartphone, XCircle,
} from "lucide-react";
import { BrandButton, CopyableField, FadeIn, StatusBadge, TextInput } from "../../../../components/ui/index.js";
import {
    formatAmount, paymentApiError, paymentStatusInfo, refundStatusInfo,
    remainingTimeLabel, SPANISH_PHONE_RE,
} from "../../../../components/store/paymentStatus.js";

const TONE_ICON = {
    success: <CheckCircle2 className="w-3.5 h-3.5" />,
    error: <XCircle className="w-3.5 h-3.5" />,
    warning: <AlertTriangle className="w-3.5 h-3.5" />,
    info: <Info className="w-3.5 h-3.5" />,
    neutral: <Clock className="w-3.5 h-3.5" />,
};

function PanelShell({ children, action }) {
    return (
        <section className="glassCard rounded-2xl p-5 flex flex-col gap-4">
            <header className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-semibold flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-primary" aria-hidden />
                    Pago por Bizum
                </h2>
                {action}
            </header>
            {children}
        </section>
    );
}

/**
 * PaymentPanel — panel de pago manual del propietario del pedido.
 *
 * Muestra importe exacto, receptor, código y caducidad, y permite comunicar
 * que ya se ha pagado. Comunicar NO confirma el pago: la comprobación es
 * humana y la hace administración.
 */
export default function PaymentPanel({ orderNumber }) {
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [notIssued, setNotIssued] = useState(false);

    const [phone, setPhone] = useState("");
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    // Una clave por intento: si la red reintenta, el backend no duplica el aviso.
    const idempotencyKeyRef = useRef(null);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setLoadError("");
        try {
            const { data } = await axios.get(`/orders/mine/${orderNumber}/payment/`);
            setPayment(data);
            setNotIssued(false);
        } catch (err) {
            if (err.response?.status === 404) {
                setNotIssued(true);
                setPayment(null);
            } else {
                setLoadError(paymentApiError(err, "No se ha podido cargar la información de pago."));
            }
        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => { load(); }, [load]);

    const phoneValid = SPANISH_PHONE_RE.test(phone);
    const showPhoneError = phoneTouched && phone.length > 0 && !phoneValid;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;               // corta el doble envío
        setPhoneTouched(true);
        if (!phoneValid) return;

        if (!idempotencyKeyRef.current) {
            idempotencyKeyRef.current =
                globalThis.crypto?.randomUUID?.() ?? `report-${Date.now()}-${Math.random()}`;
        }

        setSubmitting(true);
        setSubmitError("");
        try {
            const { data } = await axios.post(
                `/orders/mine/${orderNumber}/payment/report/`,
                { payer_phone: phone.trim(), idempotency_key: idempotencyKeyRef.current },
            );
            setPayment(data);
            idempotencyKeyRef.current = null;
        } catch (err) {
            setSubmitError(paymentApiError(err, "No se ha podido registrar tu aviso. Inténtalo de nuevo."));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Carga ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <PanelShell>
                <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
                    <span className="sr-only">Cargando la información de pago…</span>
                    <div className="skeleton h-5 w-40" />
                    <div className="skeleton h-12 w-full" />
                    <div className="skeleton h-12 w-full" />
                </div>
            </PanelShell>
        );
    }

    // ── Error de carga ───────────────────────────────────────────────────────
    if (loadError) {
        return (
            <PanelShell>
                <div role="alert" className="alert alert-error">
                    <AlertTriangle className="w-5 h-5" aria-hidden />
                    <span>{loadError}</span>
                    <button type="button" className="btn btn-sm btn-ghost gap-1" onClick={() => load()}>
                        <RefreshCw className="w-4 h-4" aria-hidden />
                        Reintentar
                    </button>
                </div>
            </PanelShell>
        );
    }

    // ── Vacío: el pedido aún no tiene instrucciones ──────────────────────────
    if (notIssued || !payment) {
        return (
            <PanelShell>
                <div role="status" className="alert">
                    <Info className="w-5 h-5" aria-hidden />
                    <span>
                        Todavía no hemos preparado las instrucciones de pago de este pedido.
                        Te las haremos llegar en cuanto estén listas.
                    </span>
                </div>
            </PanelShell>
        );
    }

    const info = paymentStatusInfo(payment.status, payment.status_display);
    const remaining = remainingTimeLabel(payment.code_expires_at);
    const receiverReady = Boolean(payment.receiver_phone_snapshot);
    const canReport = Boolean(payment.can_report);

    return (
        <PanelShell
            action={
                <div className="flex items-center gap-2">
                    <StatusBadge
                        label={info.label}
                        tone={info.tone}
                        icon={TONE_ICON[info.tone]}
                        title={info.help}
                    />
                    <button
                        type="button"
                        onClick={() => load({ silent: true })}
                        className="btn btn-ghost btn-xs btn-square"
                        aria-label="Actualizar el estado del pago"
                        title="Actualizar"
                    >
                        <RefreshCw className="w-3.5 h-3.5" aria-hidden />
                    </button>
                </div>
            }
        >
            {/* Estado resuelto: mensajes honestos, sin prometer automatismos */}
            {payment.status === "confirmado" && (
                <div role="status" className="alert alert-success">
                    <CheckCircle2 className="w-5 h-5" aria-hidden />
                    <span>
                        Hemos comprobado tu pago
                        {payment.confirmed_at
                            ? ` el ${new Date(payment.confirmed_at).toLocaleDateString("es-ES")}`
                            : ""}. Seguimos con la preparación de tu pedido.
                    </span>
                </div>
            )}

            {payment.status === "rechazado" && (
                <div role="alert" className="alert alert-error">
                    <XCircle className="w-5 h-5" aria-hidden />
                    <span>
                        No hemos podido dar por bueno este pago. Nos pondremos en contacto contigo
                        para resolverlo.
                    </span>
                </div>
            )}

            {payment.status === "incidencia" && (
                <div role="alert" className="alert alert-warning">
                    <AlertTriangle className="w-5 h-5" aria-hidden />
                    <span>
                        Hemos detectado una diferencia en el pago de este pedido. Lo estamos
                        revisando a mano y te contactaremos antes de continuar.
                    </span>
                </div>
            )}

            {payment.status === "caducado" && (
                <div role="alert" className="alert alert-warning">
                    <Clock className="w-5 h-5" aria-hidden />
                    <span>
                        El código de pago ha caducado y ya no puede usarse. Escríbenos y te
                        enviaremos uno nuevo.
                    </span>
                </div>
            )}

            {payment.status === "informado" && (
                <div role="status" className="alert alert-info">
                    <Info className="w-5 h-5" aria-hidden />
                    <span>
                        Hemos recibido tu aviso. Un responsable comprobará el Bizum manualmente;
                        el pago no queda confirmado hasta entonces.
                    </span>
                </div>
            )}

            {/* Datos del pago */}
            {receiverReady ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl bg-base-200/60 p-4">
                    <CopyableField
                        label="Importe exacto"
                        value={String(payment.expected_amount)}
                        display={formatAmount(payment.expected_amount, payment.currency)}
                        emphasis
                        mono={false}
                        helpText="Envía este importe exacto en un único Bizum."
                    />
                    <CopyableField
                        label="Concepto del Bizum"
                        value={payment.payment_code}
                        emphasis
                        helpText="Escríbelo tal cual en el concepto."
                        emptyText="Sin código activo"
                    />
                    <CopyableField
                        label="Enviar a"
                        value={payment.receiver_phone_snapshot}
                        mono={false}
                        helpText={payment.receiver_name_snapshot}
                    />
                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-xs uppercase tracking-wide font-semibold text-base-content/55">
                            Validez del código
                        </span>
                        {payment.code_expires_at ? (
                            <>
                                <span className="font-medium">
                                    {new Date(payment.code_expires_at).toLocaleString("es-ES")}
                                </span>
                                <span className={`text-xs ${remaining ? "text-base-content/55" : "text-error"}`}>
                                    {remaining ? `Quedan ${remaining}` : "Caducado"}
                                </span>
                            </>
                        ) : (
                            <span className="font-medium text-base-content/40 italic">Pendiente</span>
                        )}
                    </div>
                </div>
            ) : (
                <div role="status" className="alert">
                    <Info className="w-5 h-5" aria-hidden />
                    <span>
                        Estamos terminando de configurar los datos de cobro. En cuanto estén
                        listos verás aquí el importe, el número y el código.
                    </span>
                </div>
            )}

            {/* Instrucciones */}
            {receiverReady && canReport && (
                <ol className="text-sm text-base-content/75 list-decimal list-inside flex flex-col gap-1">
                    <li>Abre tu app bancaria y haz un Bizum al número indicado.</li>
                    <li>Envía el importe exacto en un único pago.</li>
                    <li>Escribe el código en el concepto.</li>
                    <li>Vuelve aquí, indícanos el teléfono desde el que has pagado y avísanos.</li>
                </ol>
            )}

            {/* Acción "Ya he pagado" */}
            {canReport && receiverReady && (
                <FadeIn>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
                        <div className="form-control gap-1">
                            <label className="label py-0" htmlFor="payer-phone">
                                <span className="label-text text-sm font-semibold">
                                    Teléfono desde el que has hecho el Bizum
                                </span>
                            </label>
                            <TextInput
                                id="payer-phone"
                                name="payer_phone"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                placeholder="600 12 34 56"
                                value={phone}
                                disabled={submitting}
                                variant={showPhoneError ? "error" : undefined}
                                aria-invalid={showPhoneError || undefined}
                                aria-describedby="payer-phone-help"
                                leftIcon={<Smartphone className="w-4 h-4" />}
                                onChange={(event) => setPhone(event.target.value)}
                                onBlur={() => setPhoneTouched(true)}
                            />
                            <span
                                id="payer-phone-help"
                                className={`label-text-alt ${showPhoneError ? "text-error" : "text-base-content/55"}`}
                            >
                                {showPhoneError
                                    ? "Introduce un móvil español de 9 dígitos."
                                    : "Lo usamos sólo para localizar tu Bizum."}
                            </span>
                        </div>

                        {submitError && (
                            <div role="alert" className="alert alert-error py-2">
                                <AlertTriangle className="w-4 h-4" aria-hidden />
                                <span className="text-sm">{submitError}</span>
                            </div>
                        )}

                        <div className="flex items-center gap-3 flex-wrap">
                            <BrandButton type="submit" disabled={submitting || !phoneValid}>
                                {submitting && <span className="loading loading-spinner loading-xs" aria-hidden />}
                                {submitting ? "Enviando aviso…" : "Ya he pagado"}
                            </BrandButton>
                            <span className="text-xs text-base-content/55">
                                Avisarnos no confirma el pago: lo comprobamos a mano.
                            </span>
                        </div>
                    </form>
                </FadeIn>
            )}

            {/* Devoluciones registradas */}
            {(payment.refunds || []).length > 0 && (
                <div className="border-t border-base-content/10 pt-3 flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">Devoluciones</h3>
                    <ul className="flex flex-col gap-2">
                        {payment.refunds.map((refund) => {
                            const refundInfo = refundStatusInfo(refund.status, refund.status_display);
                            return (
                                <li key={refund.id} className="flex items-center justify-between gap-3 text-sm">
                                    <span className="min-w-0">
                                        <span className="font-medium">
                                            {formatAmount(refund.amount, refund.currency)}
                                        </span>
                                        <span className="text-base-content/55">
                                            {" · "}{new Date(refund.created_at).toLocaleDateString("es-ES")}
                                        </span>
                                    </span>
                                    <StatusBadge label={refundInfo.label} tone={refundInfo.tone} />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </PanelShell>
    );
}
